import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import InitContext from "../pages/InitContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faSortUp,
  faSortDown,
  faEdit,
  faEye,
  faTrash,
  faVenus,
  faMars,
  faChevronDown,
  faChevronUp,
  faXmark,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";

import {
  formatDateTime, getGenderStatistics, getIsDarkColumn, getPatientName, sortData,
  getColumnWidth, getScrollablePanelStyle, getTableScrollStyle, getTablesSectionMinHeight, getNumberStatistics,
  getDatesStatistics, areDateStatFields, areNumberStatFields, areRegAndLoginDatesForAvgAndDeviation,
  getAgeNumberStatistics, getYearOfBirthStatistics
} from "../pages/utils";

import PatientDrugs from "./PatientDrugs";
import PatientSymptoms from "./PatientSymptoms";
import PatientLabTests from "./PatientLabTests";
import PatientMeasurements from "./PatientMeasurements";
import Spinner from "../spinner/Spinner";
import ReportPrint from "../appHeader/ReportPrint";
import { faPrint } from "@fortawesome/free-solid-svg-icons/faPrint";
import PatientDataDeleteDialog from "./PatientDataDeleteDialog";
import {Consts} from "../pages/consts";
import PatientPGx from "./PatientPGx";
import {FormControl, FormControlLabel, Radio, RadioGroup, Tooltip} from "@mui/material";
import PatientDisease from "./PatientDisease";
import {sortFieldMap, statColumns, statisticsRows} from "../../resources/json/jsons";
import * as XLSX from "xlsx";

const STUDY_FILTER_MODES = {
  ALL: "all",
  SINGLE: "single",
};

const EXPORT_FORMATS = {
  XLSX: "xlsx",
  CSV: "csv",
};

const STUDY_OPTIONS = [
  "University Hospital of RWTH Aachen",
  "Universitätsklinikum Heidelberg",
  "Leiden University Medical Center",
  "Zdravstveni center Ljubljana - VIČ",
  "Zdravstveni center Ljubljana - BEŽIGRAD",
  "Ambulanta KUS",
  "ZZ Zdravje",
  "University Hospital of Patras",
  "Psychiatric Clinic BJS",
  "University Psichiatric Clinic Ljubljana",
];

const NESTED_EXPORT_SHEETS = [
  {
    key: "drugs",
    collectionKeys: ["drugs"],
    sheetNameKey: "drugs",
    fallbackSheetName: "Drugs",
    defaultHeaders: [
      "patient_id",
      Consts.STUDY_ARM,
      "product_name",
      "active_substance",
      "atc",
      "start_date",
      "end_date",
      "abdaSubstance",
      "ddis",
      "dgis",
      "risks",
      "pdmAvailable",
    ],
    headerSources: {
      patient_id: ["patient_id", "patientId"],
      product_name: ["product_name", "productName"],
      active_substance: ["active_substance", "activeSubstance"],
      start_date: ["start_date", "startDate"],
      end_date: ["end_date", "endDate"],
    },
  },
  {
    key: "diseases",
    collectionKeys: ["diseases"],
    sheetNameKey: "diseases",
    fallbackSheetName: "Diseases",
    defaultHeaders: [
      "patientId",
      Consts.STUDY_ARM,
      "start",
      "end",
      "icd10",
      "icd10name",
      "snomed",
    ],
    headerSources: {
      patientId: ["patientId", "patient_id"],
      start: ["start", "begin", "startDate"],
      end: ["end", "endDate"],
    },
  },
  {
    key: "measurements",
    collectionKeys: ["measurements"],
    sheetNameKey: "measurements",
    fallbackSheetName: "Measurements",
    defaultHeaders: [
      "patientId",
      Consts.STUDY_ARM,
      "measurementName",
      "measurementDate",
      "measurementParams",
    ],
    headerSources: {
      patientId: ["patientId", "patient_id"],
      measurementName: ["measurementName", "name"],
      measurementDate: ["measurementDate", "dateTime", "measurement_date_time"],
      measurementParams: ["measurementParams", "params", "measurementvalueList", "value_list"],
    },
  },
  {
    key: "labTests",
    collectionKeys: ["labTests", "lab_tests"],
    sheetNameKey: "lab_tests",
    fallbackSheetName: "Lab Tests",
    defaultHeaders: [
      "patientId",
      Consts.STUDY_ARM,
      "labTestName",
      "testDate",
      "testParams",
    ],
    headerSources: {
      patientId: ["patientId", "patient_id"],
      labTestName: ["labTestName", "lab_test_name"],
      testDate: ["testDate", "test_date"],
      testParams: ["testParams", "labParams", "labparameterlist"],
    },
  },
  {
    key: "symptoms",
    collectionKeys: ["symptoms", "healthComplaints"],
    sheetNameKey: "symptoms",
    fallbackSheetName: "Health Complaints",
    defaultHeaders: [
      "patientId",
      Consts.STUDY_ARM,
      "category",
      "symptom",
      "start",
      "end",
      "sufferingLevel",
    ],
    headerSources: {
      patientId: ["patientId", "patient_id"],
      category: ["category", "type"],
      symptom: ["symptom", "term"],
      start: ["start", "startDate", "outbreak_date", "begin"],
      end: ["end", "endDate", "sending_date"],
      sufferingLevel: ["sufferingLevel", "suffering_level", "feeling_now"],
    },
  },
];

const Patients = ({ parentLoading, parentErrorMessage }) => {
  const { t } = useTranslation();
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const context = useContext(InitContext);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.MED_PLAN_LAST_CHANGE,
    ascending: true, // actually means false, since calling sortData in useEffect toggle this
  });

  const [patients, setPatients] = useState(context?.patients || []);
  const [sortedPatients, setSortedPatients] = useState(patients);
  const [showPatientPGxDialog, setShowPatientPGxDialog] = useState(false);
  const [showDiseaseDialog, setShowDiseaseDialog] = useState(false);
  const [showPatientDrugsDialog, setShowPatientDrugsDialog] = useState(false);
  const [showPatientSymptomsDialog, setShowPatientSymptomsDialog] = useState(false);
  const [showPatientLabTestsDialog, setShowPatientLabTestsDialog] = useState(false);
  const [showPatientMeasurementsDialog, setShowPatientMeasurementsDialog] = useState(false);
  const [printReport, setPrintReport] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [highlightedPatientRowId, setHighlightedPatientRowId] = useState(null);
  const [patientsTableExpanded, setPatientsTableExpanded] = useState(true);
  const [statisticsTableExpanded, setStatisticsTableExpanded] = useState(true);
  const [studyFilterMode, setStudyFilterMode] = useState(STUDY_FILTER_MODES.ALL);
  const [selectedStudy, setSelectedStudy] = useState("");
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMATS.XLSX);
  const primaryTableScrollRef = useRef(null);
  const secondaryTableScrollRef = useRef(null);
  const isSyncingTableScrollRef = useRef(false);
  const spinner = parentLoading && <Spinner />;

  useEffect(() => {
    patients && sortPatients(sortConfig?.key); // Initial sort based on sortConfig
  }, [patients]);

  useEffect(() => {
    setPatients(Array.isArray(context?.patients) ? context.patients : []);
  }, [context?.patients]);

  const isPrescriber = context?.role === Consts.HCP_PRESCRIBER;
  const disabledSeeNames = context?.role === Consts.STUDY_DIRECTOR || context?.role === "admin";

  const getGenderIcon = (gender) => {
    const normalizedGender = (gender || "").toLowerCase();

    if (normalizedGender.includes("female") || normalizedGender === "f") {
      return faVenus;
    }

    if (normalizedGender.includes("male") || normalizedGender === "m") {
      return faMars;
    }

    return null;
  };

  const sortPatients = (key) => {
    const sortField = sortFieldMap[key] || key;
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;

    const sortedPats = sortData(key, patients, ascending, sortedPat => getSortValue(sortedPat, sortField));

    setSortedPatients(sortedPats);
    setSortConfig({ key, ascending });
  };

  const handleConfirmPatientDelete = async () => {
    setPatients((prevPatients) =>
      prevPatients?.filter((patient) => patient?.id !== selectedPatient?.id),
    );

    setHighlightedPatientRowId((prevRowId) => (
      prevRowId === getPatientRowIdentifier(selectedPatient) ? null : prevRowId
    ));

    setSelectedPatient(null);
    setShowConfirmDeleteDialog(false);
  };

  const handleCancelPatientDelete = () => {
    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id
          ? { ...patient, delete_requested: 0 }
          : patient,
      ),
    );

    setShowConfirmDeleteDialog(false);
  };

  const handlePrintClick = (sortedPat) => {
    setSelectedPatient(sortedPat);
    setPrintReport(true);
  }

  const getPatientRowIdentifier = (patient) => patient?.id ?? patient?.login ?? null;

  const getSortValue = (patient, sortField) => {
    if (sortField === Consts.PATIENT) {
      return getPatientName(patient, disabledSeeNames);
    }

    return sortField
      ?.split(".")
      ?.reduce((value, field) => value?.[field], patient);
  };

  const openPatientDialog = (patient, setDialogVisibility) => {
    setSelectedPatient(patient);
    setHighlightedPatientRowId(getPatientRowIdentifier(patient));
    setDialogVisibility(true);
  };

  const updatePatientPgx = (dbGenes) => {
    const maxIdGene = dbGenes?.reduce((prev, current) => current.id > prev.id ? current : prev);

    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id && maxIdGene
          ? { ...patient, pgx_test: maxIdGene?.testDate}
          : patient,
      ),
    );
  }

  const updatePatientDisease = (startDate) => {
    setPatients((prevPatients) =>
      prevPatients?.map((patient) =>
        patient?.id === selectedPatient?.id
          ? { ...patient, diseaseDate: startDate}
          : patient,
      ),
    );
  }

  const syncTableScroll = (sourceRef, targetRef) => (event) => {
    if (isSyncingTableScrollRef.current) {
      return;
    }

    if (!targetRef.current) {
      return;
    }

    isSyncingTableScrollRef.current = true;
    targetRef.current.scrollLeft = event.currentTarget.scrollLeft;

    window.requestAnimationFrame(() => {
      isSyncingTableScrollRef.current = false;
    });
  };

  const normalizeStudyValue = (value) => (value || "").trim();
  const shouldShowAllStudies = studyFilterMode === STUDY_FILTER_MODES.ALL;

  const regularPatients = sortedPatients?.length > 0
    ? sortedPatients?.filter((patient) => patient?.delete_requested !== 1)
    : [];

  const studyArmOptions = useMemo(
    () => [...new Set(
      [
        ...STUDY_OPTIONS,
        ...(regularPatients
          ?.map((patient) => normalizeStudyValue(patient?.study_id))
          ?.filter(Boolean) || []),
      ],
    )].sort((firstStudyArm, secondStudyArm) => firstStudyArm.localeCompare(secondStudyArm)),
    [regularPatients],
  );

  const displayedPatients = shouldShowAllStudies
    ? regularPatients
    : regularPatients?.filter(
      (patient) => normalizeStudyValue(patient?.study_id) === normalizeStudyValue(selectedStudy),
    ) || [];

  console.log("displayedPatients", displayedPatients);

  useEffect(() => {
    if (studyFilterMode !== STUDY_FILTER_MODES.SINGLE) {
      return;
    }

    if (studyArmOptions.length === 0) {
      if (selectedStudy !== "") {
        setSelectedStudy("");
      }

      return;
    }

    if (!studyArmOptions.includes(selectedStudy)) {
      setSelectedStudy(studyArmOptions[0]);
    }
  }, [selectedStudy, studyArmOptions, studyFilterMode]);

  const getAdjustedHeaderWidth = (header, index) => {
    const baseWidth = getColumnWidth(header, index);
    const baseWidthInPixels = Number.parseInt(baseWidth, 10);

    if (!header || Number.isNaN(baseWidthInPixels)) {
      return baseWidth;
    }

    const translatedHeader
      = t(header, { defaultValue: header });

    const longestWordLength = translatedHeader
      ?.split(/[\s/-]+/)
      ?.reduce((maxLength, word) => Math.max(maxLength, word?.length || 0), 0);

    const estimatedHeaderWidth = Math.ceil((longestWordLength || 0) * 9 + 34);
    return `${Math.max(baseWidthInPixels, estimatedHeaderWidth)}px`;
  };

  const renderTableHeaders = (isSortable = true) => (
    <tr id="patients-table-headers">
      {statColumns?.map((header, index) => {
        if (!header) {
          return (<th key={`empty-${index}`} className="w3-theme-l1"><label /></th>);
        }

        const background = getIsDarkColumn(header) ? "w3-theme-l1" : "w3-theme-l3";
        const isActiveSort = sortConfig?.key === header;
        const isAscendingSort = isActiveSort && sortConfig?.ascending;

        const translatedHeader
          = t(header, { defaultValue: header });

        return (
          <th
            key={`${header}-${index}`}
            className={`${background} sort-header ${header === "Last Login" ? "last-login-column" : ""}`}
          >
            {isSortable
              ? <button
                  type="button"
                  onClick={() => sortPatients(header)}
                  className={`patients-sort-button ${isActiveSort ? "is-active" : ""}`}
                  aria-label={`${translatedHeader} ${t("sort", { defaultValue: "sort" })}`}
                >
                  <span className="patients-sort-button__label" title={translatedHeader}>{translatedHeader}</span>
                  <span className={`patients-sort-indicator ${isActiveSort ? "is-active" : ""}`} aria-hidden="true">
                    <FontAwesomeIcon
                      icon={faSortUp}
                      className={`patients-sort-indicator__icon ${isAscendingSort ? "is-selected" : ""}`}
                    />
                    <FontAwesomeIcon
                      icon={faSortDown}
                      className={`patients-sort-indicator__icon ${isActiveSort && !sortConfig?.ascending ? "is-selected" : ""}`}
                    />
                  </span>
                </button>
              : <span className="patients-table-header-label" title={translatedHeader}>{translatedHeader}</span>
            }
          </th>
        );
      })}
    </tr>
  );

  const renderTableColumns = () => (
    <colgroup>
      {statColumns?.map((header, index) => {
        const adjustedHeaderWidth = getAdjustedHeaderWidth(header, index);

        return (
          <col key={`col-${header || "empty"}-${index}`}
            style={{ width: adjustedHeaderWidth, minWidth: adjustedHeaderWidth }}/>
        );
      })}
    </colgroup>
  );

  const getStatisticsCellValue = (row, header, columnIndex) => {
    if (columnIndex === 0) {
      return row.firstColumnLabel;
    }

    if (!header) { // for the column with the print icon, which has an empty header
      return "";
    }

    if (!shouldShowAllStudies && displayedPatients?.length === 0) {
      return "";
    }

    const columnKey = header || `column_${columnIndex}`;

    if (header === Consts.GENDER) {
      return getGenderStatistics(row.key, displayedPatients?.length, malePatients, femalePatients);
    }

    if (header ===  Consts.STUDY_ARM || header === Consts.MED_PLAN_LAST_CHANGE) {
      return "-";
    }

    if(header === Consts.REG_AGE){
      const patientsWithValidAges= displayedPatients?.length > 0
        ? displayedPatients?.filter(p => p.age !== null && p.age !== undefined && p.age !== '-')
        : [];

      const ages = patientsWithValidAges?.length > 0 ? patientsWithValidAges?.map(p => p.age) : [];
      return getAgeNumberStatistics(row.key, ages, displayedPatients?.length);
    }

    if(header === Consts.YEAR_OF_BIRTH){
      const patientsWithValidYear= displayedPatients?.length > 0
        ? displayedPatients?.filter(p => p.year_of_birth !== null && p.year_of_birth !== undefined && p.year_of_birth !== '-')
        : [];

      const yearsOfBirth = patientsWithValidYear?.length > 0 ? patientsWithValidYear?.map(p => p.year_of_birth) : [];
      return getYearOfBirthStatistics(row.key, yearsOfBirth, displayedPatients?.length);
    }

    if(areDateStatFields(header)){
      let filteredPatients;
      let dates;

      if(header === Consts.REGISTRATION_DATE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.registration_date !== null && p.registration_date !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.registration_date));
      } else if(header === Consts.LAST_LOGIN){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.lastLogin !== null && p.lastLogin !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.lastLogin));
      } else if(header === Consts.DISEASES_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.diseaseDate !== null && p.diseaseDate !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.diseaseDate));
      } else if(header === Consts.PGX_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.pgx_test !== null && p.pgx_test !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.pgx_test));
      } else if(header === Consts.SYMPTOMS_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.last_symptom !== null && p.last_symptom !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.last_symptom));
      } else if(header === Consts.LAB_TESTS_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.last_lab_test !== null && p.last_lab_test !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.last_lab_test));
      } else if(header === Consts.LAB_TESTS_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.last_lab_test !== null && p.last_lab_test !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.last_lab_test));
      } else if(header === Consts.MEASUREMENTS_LAST_CHANGE){
        filteredPatients = displayedPatients?.length > 0
          ? displayedPatients?.filter(p => p.last_measurement !== null && p.last_measurement !== undefined)
          : [];

        dates = filteredPatients.map(p => new Date(p.last_measurement));
      }

      if(areRegAndLoginDatesForAvgAndDeviation(header, row.key)){
        return '-';
      }

      const number = getDatesStatistics(row.key, dates, displayedPatients.length);
      return !Number.isNaN(number) ? number : '-';
    }

    if(areNumberStatFields(header)){
      let selectedNumbers = [];

      if (displayedPatients?.length > 0) {
        if (header === Consts.NUMBER_LOGINS) {
          selectedNumbers = displayedPatients?.map(p => p.numberLogins);
        } else if (header === Consts.LAST_LOGIN_DAYS) {
          selectedNumbers = displayedPatients?.map(p => p.lastLoginDays ? p.lastLoginDays : 0);
        } else if (header === Consts.LAST_DISEASE_DAYS) {
          selectedNumbers = displayedPatients?.map(p => p.lastDiseaseDays ? p.lastDiseaseDays : 0);
        } else if (header === Consts.LAST_MEASUREMENT_DAYS) {
          selectedNumbers = displayedPatients?.map(p => p.lastMeasurementDays ? p.lastMeasurementDays : 0);
        } else if (header === Consts.NUMBER_DISEASES) {
          selectedNumbers = displayedPatients?.map(p => p.numberDiseases);
        } else if (header === Consts.YEAR_OF_BIRTH) {
          selectedNumbers = displayedPatients?.map(p => p.year_of_birth);
        } else if (header === Consts.NUMBER_GENES) {
          selectedNumbers = displayedPatients?.map(p => p.numberGenes);
        } else if (header === Consts.NUMBER_ACTIONABLE_GENES) {
          selectedNumbers = displayedPatients?.map(p => p.numberActionableGenes);
        } else if (header === Consts.NUMBER_DRUGS) {
          selectedNumbers = displayedPatients?.map(p => p.drugsNumber);
        } else if (header === Consts.NUMBER_DRUGS_INITIAL) {
          selectedNumbers = displayedPatients?.map(p => p.drugsNumberInitial);
        } else if (header === Consts.NUMBER_NEW_ADDED_DRUGS) {
          selectedNumbers = displayedPatients?.map(p => p.newAddedDrugsNumber);
        } else if (header === Consts.NUMBER_DELETED_DRUGS) {
          selectedNumbers = displayedPatients?.map(p => p.deletedDrugsNumber);
        } else if (header === Consts.NUMBER_MED_PLAN_CHANGES) {
          selectedNumbers = displayedPatients?.map(p => p.changedMedPlanNumber);
        } else if (header === Consts.NUMBER_DDIS) {
          selectedNumbers = displayedPatients?.map(p => p.numberDdis);
        } else if (header === Consts.NUMBER_DGIS) {
          selectedNumbers = displayedPatients?.map(p => p.numberDgis);
        } else if (header.indexOf("risk_combined") >= 0) {
          if(header.indexOf("less_10") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.less10);
          } else if(header.indexOf("from_10_to_20") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.from10to20);
          } else if(header.indexOf("from_20_to_30") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.from20to30);
          } else if(header.indexOf("from_30_to_40") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.from30to40);
          } else if(header.indexOf("from_40_to_50") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.from40to50);
          } else if(header.indexOf("greater_50") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.totals?.greater50);
          }
        } else if (header.indexOf("risk_relative") >= 0) {
          if(header.indexOf("less_10") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.less10);
          } else if(header.indexOf("from_10_to_20") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.from10to20);
          } else if(header.indexOf("from_20_to_30") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.from20to30);
          } else if(header.indexOf("from_30_to_40") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.from30to40);
          } else if(header.indexOf("from_40_to_50") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.from40to50);
          } else if(header.indexOf("greater_50") >= 0){
            selectedNumbers = displayedPatients?.map(p => p.sortedRiskNumbers?.diffs?.greater50);
          }
        } else if (header === Consts.NUMBER_PDMS) {
          selectedNumbers = displayedPatients?.map(p => p.numberPdms);
        } else if (header === Consts.NUMBER_SYMPTOMS) {
          selectedNumbers = displayedPatients?.map(p => p.numberSymptoms);
        } else if (header === Consts.NUMBER_DIFF_SYMPTOMS) {
          selectedNumbers = displayedPatients?.map(p => p.numberSymptoms);
        } else if (header === Consts.NUMBER_OTHER_SYMPTOMS) {
          selectedNumbers = displayedPatients?.map(p => p.numberSymptoms);
        } else if (header === Consts.NUMBER_LAB_TESTS) {
          selectedNumbers = displayedPatients?.map(p => p.numberLabTests);
        } else if (header === Consts.NUMBER_MEASUREMENTS) {
          selectedNumbers = displayedPatients?.map(p => p.numberMeasurements);
        }

        const number = getNumberStatistics(row.key, selectedNumbers);
        return !Number.isNaN(number) ? number : '-';
      }
    }

    return row.values?.[columnKey] ?? "-";
  };

  const getPatientExportCellValue = (patient, header) => {
    if (header === Consts.PATIENT) {
      return getPatientName(patient, disabledSeeNames);
    } else if (header === Consts.STUDY_ARM) {
      return patient?.study_id;
    } else if (header === Consts.GENDER) {
      return patient?.gender;
    } else if (header === Consts.REG_AGE) {
      return patient?.age;
    } else if (header === Consts.YEAR_OF_BIRTH) {
      return patient?.year_of_birth;
    } else if (header === Consts.REGISTRATION_DATE) {
      return patient?.registration_date
        ? formatDateTime(patient?.registration_date, true)
        : t("not_completed");
    } else if (header === Consts.LAST_LOGIN) {
      return formatDateTime(patient?.lastLogin, true);
    } else if (header === Consts.LAST_LOGIN_DAYS) {
      return patient?.lastLoginDays ? patient?.lastLoginDays : 0;
    } else if (header === Consts.LAST_DISEASE_DAYS) {
      return patient?.lastDiseaseDays ? patient?.lastDiseaseDays : 0;
    } else if (header === Consts.LAST_MEASUREMENT_DAYS) {
      return patient?.lastMeasurementDays ? patient?.lastMeasurementDays : 0;
    } else if (header === Consts.NUMBER_LOGINS) {
      return patient?.numberLogins;
    } else if (header === Consts.DISEASES_LAST_CHANGE) {
      return formatDateTime(patient?.diseaseDate, true);
    } else if (header === Consts.NUMBER_DISEASES) {
      return patient?.numberDiseases;
    } else if (header === Consts.PGX_LAST_CHANGE) {
      return formatDateTime(patient?.pgx_test, true);
    } else if (header === Consts.NUMBER_GENES) {
      return patient?.numberGenes ? patient?.numberGenes : 0;
    } else if (header === Consts.NUMBER_ACTIONABLE_GENES) {
      return patient?.numberActionableGenes ? patient?.numberActionableGenes : 0;
    } else if (header === Consts.MED_PLAN_LAST_CHANGE) {
      return formatDateTime(patient?.last_prescription, true);
    } else if (header === Consts.NUMBER_DRUGS) {
      return patient?.drugsNumber;
    } else if (header === Consts.NUMBER_DRUGS_INITIAL) {
      return patient?.drugsNumberInitial;
    } else if (header === Consts.NUMBER_NEW_ADDED_DRUGS) {
      return patient?.newAddedDrugsNumber;
    } else if (header === Consts.NUMBER_DELETED_DRUGS) {
      return patient?.deletedDrugsNumber;
    } else if (header === Consts.NUMBER_MED_PLAN_CHANGES) {
      return patient?.changedMedPlanNumber;
    } else if (header === Consts.NUMBER_DDIS) {
      return patient?.numberDdis;
    } else if (header === Consts.NUMBER_DGIS) {
      return patient?.numberDgis;
    } else if (header === Consts.NUMBER_DDGIS) {
      return patient?.ddgiDrugsNumber ? patient?.ddgiDrugsNumber : 0;
    } else if (header.indexOf("risk_combined") >= 0) {
      if(header.indexOf("less_10") >= 0){
        return patient?.sortedRiskNumbers?.totals?.less10; // todo
      } else if(header.indexOf("from_10_to_20") >= 0){
        return patient?.sortedRiskNumbers?.totals?.from10to20; // todo
      } else if(header.indexOf("from_20_to_30") >= 0){
        return patient?.sortedRiskNumbers?.totals?.from20to30; // todo
      } else if(header.indexOf("from_30_to_40") >= 0){
        return patient?.sortedRiskNumbers?.totals?.from30to40; // todo
      } else if(header.indexOf("from_40_to_50") >= 0){
        return patient?.sortedRiskNumbers?.totals?.from40to50; // todo
      } else if(header.indexOf("greater_50") >= 0){
        return patient?.sortedRiskNumbers?.totals?.greater50; // todo
      }
    } else if (header.indexOf("risk_relative") >= 0) {
      if(header.indexOf("less_10") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.less10; // todo
      } else if(header.indexOf("from_10_to_20") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.from10to20; // todo
      } else if(header.indexOf("from_20_to_30") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.from20to30; // todo
      } else if(header.indexOf("from_30_to_40") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.from30to40; // todo
      } else if(header.indexOf("from_40_to_50") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.from40to50; // todo
      } else if(header.indexOf("greater_50") >= 0){
        return patient?.sortedRiskNumbers?.diffs?.greater50; // todo
      }
    } else if (header === Consts.NUMBER_PDMS) {
      return patient?.numberPdms;
    } else if (header === Consts.SYMPTOMS_LAST_CHANGE) {
      return formatDateTime(patient?.last_symptom, true);
    } else if (header === Consts.NUMBER_SYMPTOMS) {
      return patient?.numberSymptoms;
    } else if (header === Consts.NUMBER_DIFF_SYMPTOMS) {
      return patient?.numberDiffSymptoms; // TODO
    } else if (header === Consts.NUMBER_OTHER_SYMPTOMS) {
      return patient?.numberOtherSymptoms;// TODO
    } else if (header === Consts.LAB_TESTS_LAST_CHANGE) {
      return formatDateTime(patient?.last_lab_test, true);
    } else if (header === Consts.NUMBER_LAB_TESTS) {
      return patient?.numberLabTests;
    } else if (header === Consts.MEASUREMENTS_LAST_CHANGE) {
      return formatDateTime(patient?.last_measurement, true);
    } else if (header === Consts.NUMBER_MEASUREMENTS) {
      return patient?.numberMeasurements;
    }

    return "";
  };

  const normalizeExportValue = (value) => (value === null || value === undefined
    ? ""
    : String(value));

  const escapeCsvValue = (value) => {
    const normalizedValue = normalizeExportValue(value);

    if (/[",\n]/.test(normalizedValue)) {
      return `"${normalizedValue.replace(/"/g, "\"\"")}"`;
    }

    return normalizedValue;
  };

  const getSafeExcelSheetName = (sheetName, fallbackSheetName) => {
    const normalizedSheetName = normalizeExportValue(sheetName)
      .replace(/[\\/:*?[\]]/g, "_")
      .trim();

    if (!normalizedSheetName) {
      return fallbackSheetName;
    }

    return normalizedSheetName.slice(0, 31);
  };

  const getNestedExportRows = (sheetConfig) => displayedPatients?.reduce((rows, patient) => {
    const collectionKeys = sheetConfig?.collectionKeys || [sheetConfig?.key];
    const patientRows = collectionKeys.reduce((acc, collectionKey) => {
      if (!Array.isArray(patient?.[collectionKey]) || patient[collectionKey].length === 0) {
        return acc;
      }

      const rowsWithPatientId = patient[collectionKey]?.map((entry) => ({
        ...entry,
        __exportPatientId: patient?.id,
        __exportStudyArm: patient?.study_id,
      }));

      return [...acc, ...rowsWithPatientId];
    }, []);

    return [...rows, ...patientRows];
  }, []) || [];

  const getNestedExportCellValue = (row, header, headerSources = {}) => {
    const aliases = headerSources?.[header] || [header];

    for (let index = 0; index < aliases.length; index += 1) {
      const alias = aliases[index];
      const value = row?.[alias];

      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }

    if ((header === "patientId" || header === "patient_id") && row?.__exportPatientId) {
      return row.__exportPatientId;
    }

    if (header === Consts.STUDY_ARM) {
      return row?.__exportStudyArm || "";
    }

    return "";
  };

  const normalizeNestedExportValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  };

  const downloadExportFile = (fileContent, mimeType, fileName) => {
    const blob = new Blob([fileContent], { type: mimeType });
    const exportUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = exportUrl;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(exportUrl);
  };

  const handleDataExport = (format) => {
    const exportHeaders = statColumns?.filter((header) => header) || [];

    const translatedPatientHeaders = exportHeaders
      .map((header) => t(header, { defaultValue: header }));

    const patientRows = displayedPatients?.map((patient) =>
      exportHeaders.map((header) => getPatientExportCellValue(patient, header)),
    ) || [];

    const statisticsHeaders = [
      t("statistics_metric", { defaultValue: "Statistic" }),
      ...translatedPatientHeaders.slice(1),
    ];

    const statisticsRowsForExport = statisticsRows?.map((row) => ([
      t(row.firstColumnLabel, { defaultValue: row.firstColumnLabel }),
      ...exportHeaders
        .slice(1)
        .map((header, index) => getStatisticsCellValue(row, header, index + 1)),
    ])) || [];

    const nestedExportSections = NESTED_EXPORT_SHEETS.map((sheetConfig) => {
      const nestedRows = getNestedExportRows(sheetConfig);
      const nestedHeaders = sheetConfig.defaultHeaders || [];
      const nestedDataRows = nestedRows.map((row) =>
        nestedHeaders.map((header) =>
          normalizeNestedExportValue(
            getNestedExportCellValue(row, header, sheetConfig.headerSources),
          )),
      );

      return {
        ...sheetConfig,
        nestedHeaders,
        nestedDataRows,
      };
    });

    const studyScope = shouldShowAllStudies
      ? "all-study-arms"
      : normalizeStudyValue(selectedStudy) || "selected-study-arm";

    const safeStudyScope = studyScope.replace(/[^\w-]+/g, "_");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const baseFileName = `patients-statistics-${safeStudyScope}-${timestamp}`;

    if (format === EXPORT_FORMATS.XLSX) {
      const patientsSheetData = [
        translatedPatientHeaders,
        ...patientRows.map((row) => row.map((cell) => normalizeExportValue(cell))),
      ];

      const statisticsSheetData = [
        statisticsHeaders,
        ...statisticsRowsForExport.map((row) => row.map((cell) => normalizeExportValue(cell))),
      ];

      const workbook = XLSX.utils.book_new();
      const patientsSheet = XLSX.utils.aoa_to_sheet(patientsSheetData);
      const statisticsSheet = XLSX.utils.aoa_to_sheet(statisticsSheetData);

      const patientsSheetName = getSafeExcelSheetName(
        t("my_patients", { defaultValue: "My patients" }),
        "Patients",
      );

      const statisticsSheetName = getSafeExcelSheetName(
        t("statistics", { defaultValue: "Statistics" }),
        "Statistics",
      );

      XLSX.utils.book_append_sheet(workbook, patientsSheet, patientsSheetName);
      XLSX.utils.book_append_sheet(workbook, statisticsSheet, statisticsSheetName);

      nestedExportSections.forEach((sheetConfig) => {
        const nestedSheetData = [
          sheetConfig.nestedHeaders,
          ...sheetConfig.nestedDataRows,
        ];

        const nestedSheet = XLSX.utils.aoa_to_sheet(nestedSheetData);
        const nestedSheetName = getSafeExcelSheetName(
          t(sheetConfig.sheetNameKey, { defaultValue: sheetConfig.fallbackSheetName }),
          sheetConfig.fallbackSheetName,
        );

        XLSX.utils.book_append_sheet(workbook, nestedSheet, nestedSheetName);
      });

      XLSX.writeFile(workbook, `${baseFileName}.xlsx`);
      return;
    }

    const csvLines = [];
    csvLines.push(t("my_patients", { defaultValue: "My patients" }));
    csvLines.push(translatedPatientHeaders.map((header) => escapeCsvValue(header)).join(","));

    patientRows.forEach((row) => {
      csvLines.push(row.map((cell) => escapeCsvValue(cell)).join(","));
    });

    csvLines.push("");
    csvLines.push(t("statistics", { defaultValue: "Statistics" }));
    csvLines.push(statisticsHeaders.map((header) => escapeCsvValue(header)).join(","));

    statisticsRowsForExport.forEach((row) => {
      csvLines.push(row.map((cell) => escapeCsvValue(cell)).join(","));
    });

    nestedExportSections.forEach((sheetConfig) => {
      csvLines.push("");
      csvLines.push(t(sheetConfig.sheetNameKey, { defaultValue: sheetConfig.fallbackSheetName }));
      csvLines.push(sheetConfig.nestedHeaders.map((header) => escapeCsvValue(header)).join(","));

      sheetConfig.nestedDataRows.forEach((row) => {
        csvLines.push(row.map((cell) => escapeCsvValue(cell)).join(","));
      });
    });

    downloadExportFile(
      csvLines.join("\n"),
      "text/csv;charset=utf-8;",
      `${baseFileName}.csv`,
    );
  };

  const malePatients = displayedPatients?.length > 0
    ? displayedPatients?.filter((patient) => patient?.gender === "male")?.length
    : 0;

  const femalePatients = displayedPatients?.length > 0
    ? displayedPatients?.filter((patient) => patient?.gender === "female")?.length
    : 0;

  const renderStatTableBody = () => (statisticsRows.map((row, rowIndex) => (
    <tr id={`statistics-table-row-${row.key || rowIndex}`}>
      {statColumns?.map((header, columnIndex) => {
        const background = columnIndex % 2 === 0 ? "w3-theme-l1" : "w3-theme-l3";
        const cellClassName = `${background} last-login-cell`;
        let cellValue = getStatisticsCellValue(row, header, columnIndex);

        return (
          <td key={`placeholder-${row.key || rowIndex}-${header || "empty"}-${columnIndex}`} className={cellClassName}>
            <label>{!cellValue || cellValue === Infinity || cellValue === -Infinity ? "-" : cellValue}</label>
          </td>
        );
      })}
    </tr>
  )));

  const renderPatientsRows = (patientsList) => (
    patientsList?.map((sortedPat, index) => {
      const patientRowId = getPatientRowIdentifier(sortedPat);
      const isHighlightedRow = patientRowId && patientRowId === highlightedPatientRowId;

      return (
        <tr id={`patients-table-row-${patientRowId || index}`}
          key={`${patientRowId || "patient"}-${index}`}
          className={isHighlightedRow ? "patients-table-row patients-table-row--selected" : "patients-table-row"}
        >
          <td className="w3-theme-l1 last-login-cell">
            <label>{getPatientName(sortedPat, disabledSeeNames)}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell patient-study-branch-cell">
            <label>{sortedPat?.study_id}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            {getGenderIcon(sortedPat?.gender) && (
              <FontAwesomeIcon icon={getGenderIcon(sortedPat?.gender)} className="patient-gender-icon" />
            )}
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.age}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.year_of_birth}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>
              {sortedPat?.registration_date
                ? formatDateTime(sortedPat?.registration_date, true)
                : t("not_completed")}
            </label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{formatDateTime(sortedPat?.lastLogin, true)}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.lastLoginDays ? sortedPat?.lastLoginDays : 0}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberLogins}</label>
          </td>
          <td className="w3-theme-l3 date-action-cell">
            <label>{formatDateTime(sortedPat?.diseaseDate, true)}</label>
            {isPrescriber && <button
              type="button"
              onClick={() => {
                openPatientDialog(sortedPat, setShowDiseaseDialog);
              } }
              className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
              aria-label={t("edit", { defaultValue: "Edit" })}
            >
              <FontAwesomeIcon icon={faEdit}/>
            </button>}
            {!isPrescriber && sortedPat?.diseaseDate && <button
              type="button"
              onClick={() => {
                openPatientDialog(sortedPat, setShowDiseaseDialog);
              }}
              className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
              aria-label={t("view", { defaultValue: "View" })}
            >
              <FontAwesomeIcon icon={faEye}/>
            </button>}
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.lastDiseaseDays ? sortedPat?.lastDiseaseDays : 0}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.numberDiseases}</label>
          </td>
          <td className="w3-theme-l1 date-action-cell">
            <label>{formatDateTime(sortedPat?.pgx_test, true)}</label>
            {isPrescriber && <button
              type="button"
              onClick={() => {
                openPatientDialog(sortedPat, setShowPatientPGxDialog);
              }}
              className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
              aria-label={t("edit", { defaultValue: "Edit" })}
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>}
            {!isPrescriber && sortedPat?.pgx_test && <button
              type="button"
              onClick={() => {
                openPatientDialog(sortedPat, setShowPatientPGxDialog);
              }}
              className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
              aria-label={t("view", { defaultValue: "View" })}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>}
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.numberGenes ? sortedPat?.numberGenes : 0}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberActionableGenes ? sortedPat?.numberActionableGenes : 0}</label>
          </td>
          <td className="w3-theme-l3 date-action-cell">
            {isPrescriber && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientDrugsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
                aria-label={t("edit", { defaultValue: "Edit" })}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            {!isPrescriber && sortedPat?.last_prescription && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientDrugsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
                aria-label={t("view", { defaultValue: "View" })}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
            {!isPrescriber && !sortedPat?.last_prescription && (
              <label>-</label>
            )}
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.drugsNumberInitial}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.drugsNumber}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.newAddedDrugsNumber}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.deletedDrugsNumber}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.changedMedPlanNumber}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.numberDdis}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberDgis}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.ddgiDrugsNumber}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.less10}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.from10to20}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.from20to30}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.from30to40}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.from40to50}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.diffs?.greater50}</label>
          </td>

          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.less10}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.from10to20}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.from20to30}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.from30to40}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.from40to50}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.sortedRiskNumbers?.totals?.greater50}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberPdms}</label>
          </td>
          <td className="w3-theme-l3 date-action-cell">
            <label>{formatDateTime(sortedPat?.last_symptom, true)}</label>
            {isPrescriber && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientSymptomsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
                aria-label={t("edit", { defaultValue: "Edit" })}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            {!isPrescriber && sortedPat?.last_symptom && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientSymptomsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
                aria-label={t("view", { defaultValue: "View" })}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberSymptoms}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.numberDiffSymptoms}</label>
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberOtherSymptoms}</label>
          </td>
          <td className="w3-theme-l3 date-action-cell">
            <label>{formatDateTime(sortedPat?.last_lab_test, true)}</label>
            {isPrescriber && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientLabTestsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
                aria-label={t("edit", { defaultValue: "Edit" })}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            {!isPrescriber && sortedPat?.last_lab_test && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientLabTestsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
                aria-label={t("view", { defaultValue: "View" })}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.numberLabTests}</label>
          </td>
          <td className="w3-theme-l3 date-action-cell">
            <label>{formatDateTime(sortedPat?.last_measurement, true)}</label>
            {isPrescriber && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientMeasurementsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary w3-right"
                aria-label={t("edit", { defaultValue: "Edit" })}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            {!isPrescriber && sortedPat?.last_measurement && (
              <button
                type="button"
                onClick={() => {
                  openPatientDialog(sortedPat, setShowPatientMeasurementsDialog);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--primary w3-right"
                aria-label={t("view", { defaultValue: "View" })}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
          </td>
          <td className="w3-theme-l1 last-login-cell">
            <label>{sortedPat?.lastMeasurementDays ? sortedPat?.lastMeasurementDays : 0}</label>
          </td>
          <td className="w3-theme-l3 last-login-cell">
            <label>{sortedPat?.numberMeasurements}</label>
          </td>
          <td className="w3-theme-l1">
            <Tooltip title={t("export")} placement="top"
                     slotProps={{tooltip: {sx: {fontSize: '1.3rem'}}}}>
              <button onClick={() => handlePrintClick(sortedPat)}
                      type="button"
                      className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--print"
                      aria-label={t("export", { defaultValue: "Export" })}
                      disabled={printReport}>
                <FontAwesomeIcon icon={faPrint} />
              </button>
            </Tooltip>
            { sortedPat?.delete_requested === 1 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPatient(sortedPat);
                  setShowConfirmDeleteDialog(true);
                }}
                className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--danger w3-right"
                aria-label={t("delete", { defaultValue: "Delete" })}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </td>
        </tr>
      );
    })
  );

  const renderPatientsTable
    = (patientsList, emptyMessage, maxVisibleRows, hasHeader = true) => (
    patientsList?.length > 0 ? (
      <div
        className="w3-responsive patients-table-scroll"
        ref={primaryTableScrollRef}
        onScroll={syncTableScroll(primaryTableScrollRef, secondaryTableScrollRef)}
        style={getTableScrollStyle(patientsList.length, maxVisibleRows, hasHeader)}
      >
        <table id="patients-table" className="w3-table patients-table">
          {renderTableColumns()}
          <thead className="w3-border-bottom w3-border-blue row-header">
            {renderTableHeaders(true)}
          </thead>
          <tbody id="patients-table-body">
            {renderPatientsRows(patientsList)}
          </tbody>
        </table>
      </div>
    ) : (
      !spinner && (<p className="w3-center">{emptyMessage}</p>)
    )
  );

  const patientsTableContent
    = renderPatientsTable(
      displayedPatients,
      shouldShowAllStudies
        ? t("you_have_not_yet_any_patients")
        : t("no_patients_for_selected_study", { defaultValue: "No patients found for the selected study." }),
      6,
      true,
    );

  const statisticsTableContent = (
    <div
      className="w3-responsive patients-table-scroll patients-table-scroll--secondary"
      ref={secondaryTableScrollRef}
      onScroll={syncTableScroll(secondaryTableScrollRef, primaryTableScrollRef)}
      style={getTableScrollStyle(statisticsRows.length, 5, true)}
    >
      <table id="statistics-table" className="w3-table patients-table patients-table--secondary">
        {renderTableColumns()}
        <thead className="w3-border-bottom w3-border-blue row-header">
          {renderTableHeaders(false)}
        </thead>
        <tbody id="statistics-table-body">
          {renderStatTableBody()}
        </tbody>
      </table>
    </div>
  );

  return (
    <div id="upper" title="upper" className="w3-display-container content-container content-container-full">
      <div id="sub_upper" title="sub_upper" className="medication-view">
        <div id="sub_upper_label" title="sub_upper_label" style={{ width: '100%', textAlign: 'right', paddingRight: "20px" }}>
          <label>{t("username")}: {context?.username}, {t("role")}: {context?.role}</label>
        </div>
        <div id="sub_upper_content" title="sub_upper_content" className="w3-center form-display-drugs medication-content">
          <div id="sub_upper_sub_content" title="sub_upper_sub_content" className="w3-panel w3-card-4 record-panel">
            {showDiseaseDialog && (
              <PatientDisease patient={selectedPatient} updatePatient={updatePatientDisease}
                              showDialog={showDiseaseDialog}
                              onCloseDialog={() => setShowDiseaseDialog(false)} />
            )}
            {showPatientPGxDialog && (
              <PatientPGx patient={selectedPatient} updatePatient={updatePatientPgx} showDialog={showPatientPGxDialog}
                          onCloseDialog={() => setShowPatientPGxDialog(false)} />
            )}
            {showPatientDrugsDialog && (
              <PatientDrugs
                showDialog={showPatientDrugsDialog}
                onCloseDialog={() => setShowPatientDrugsDialog(false)}
                patient={selectedPatient}
                disabledSeeNames={disabledSeeNames}
              />
            )}
            {showPatientSymptomsDialog && (
              <PatientSymptoms
                showDialog={showPatientSymptomsDialog}
                onCloseDialog={() => setShowPatientSymptomsDialog(false)}
                patient={selectedPatient}
                disabledSeeNames={disabledSeeNames}
              />
            )}
            {showPatientLabTestsDialog && (
              <PatientLabTests
                showDialog={showPatientLabTestsDialog}
                onCloseDialog={() => setShowPatientLabTestsDialog(false)}
                patient={selectedPatient}
                disabledSeeNames={disabledSeeNames}
              />
            )}
            {showPatientMeasurementsDialog && (
              <PatientMeasurements
                showDialog={showPatientMeasurementsDialog}
                onCloseDialog={() => setShowPatientMeasurementsDialog(false)}
                patient={selectedPatient}
                disabledSeeNames={disabledSeeNames}
              />
            )}
            {printReport && (
              <ReportPrint selectedPatient={selectedPatient}
                           onFinishPrintReport={() => setPrintReport(false)} />
            )}
            {showConfirmDeleteDialog && (
              <PatientDataDeleteDialog
                showDialog={showConfirmDeleteDialog}
                patient={selectedPatient}
                onCancelDialog={() => setShowConfirmDeleteDialog(false)}
                onConfirmPatientDelete={handleConfirmPatientDelete}
                onCancelPatientDelete={handleCancelPatientDelete}
              />
            )}
            <header id="patients_header" title="patients_header" className="w3-container w3-center">
              <div className="w3-display-container w3-padding-small"><h2>{t("my_patients")}</h2></div>
            </header>
            {showDisclaimer && (
              <div id="disclaimer" title="disclaimer" className="w3-row-padding medication-tabular__disclaimer-wrap">
                <div className="w3-panel w3-card-4 disclaimer-background medication-tabular__disclaimer-card medication-tabular__disclaimer-panel">
                  <div className="medication-tabular__disclaimer-header">
                    <span className="medication-tabular__disclaimer-title">⚠️{t("title_disclaimer_hcp")}</span>
                    <FontAwesomeIcon
                      icon={faXmark}
                      className="modal-close medication-tabular__disclaimer-close"
                      onClick={() => setShowDisclaimer(false)}
                    />
                  </div>
                </div>
              </div>
            )}
            {parentErrorMessage}
            {spinner}
            {context?.role === "admin" && <div className="patients-study-filter">
              <label className="patients-study-filter__title">
                {t("study_arm_filter", { defaultValue: "Study arm filter"})}
              </label>
              <FormControl component="fieldset" className="patients-study-filter__mode">
                <RadioGroup
                  row
                  aria-label={t("study_arm_filter", {defaultValue: "Study arm filter"})}
                  name="patients-study-filter"
                  value={studyFilterMode}
                  onChange={(event) => setStudyFilterMode(event.target.value)}
                >
                  <FormControlLabel
                    value={STUDY_FILTER_MODES.ALL}
                    control={<Radio/>}
                    label={t("all_study_arms", {defaultValue: "All study arms"})}
                  />
                  <FormControlLabel
                    value={STUDY_FILTER_MODES.SINGLE}
                    control={<Radio/>}
                    label={t("select_one_study_arm", {defaultValue: "Select one study arm"})}
                  />
                </RadioGroup>
              </FormControl>
              {studyFilterMode === STUDY_FILTER_MODES.SINGLE && (
                <div id="filter" title="filter" className="patients-study-filter__select-wrap">
                  <label className="patients-study-filter__label" htmlFor="patients-study-select">
                    {t("study_arm", {defaultValue: "Study arm"})}
                  </label>
                  <select
                    id="patients-study-select"
                    className="select_box_style patients-study-filter__select"
                    value={selectedStudy}
                    onChange={(event) => setSelectedStudy(event.target.value)}
                  >
                    {studyArmOptions.map((studyOption) => (
                      <option key={studyOption} value={studyOption}>
                        {studyOption}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div id="filter_export" title="filter_export" className="patients-study-filter__export-wrap">
                <label className="patients-study-filter__label" htmlFor="patients-export-format">
                  {t("export_format", { defaultValue: "Export format" })}
                </label>
                <select
                  id="patients-export-format"
                  className="select_box_style patients-study-filter__select patients-study-filter__export-select"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value)}
                >
                  <option value={EXPORT_FORMATS.XLSX}>XLSX</option>
                  <option value={EXPORT_FORMATS.CSV}>CSV</option>
                </select>
                <Tooltip
                  title={t("export_patients_data", { defaultValue: "Export patients and statistics" })}
                  placement="top"
                  slotProps={{ tooltip: { sx: { fontSize: "1.3rem" } } }}
                >
                  <button
                    type="button"
                    className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--print"
                    onClick={() => handleDataExport(exportFormat)}
                    aria-label={t("export_patients_data", { defaultValue: "Export patients and statistics" })}
                  >
                    <FontAwesomeIcon icon={faFileExport} />
                  </button>
                </Tooltip>
              </div>
            </div>}
            <div id="table_togle" title="table_togle" className="patients-tables-section w3-margin-bottom" style={{ minHeight: getTablesSectionMinHeight() }}>
              <div id="pat_table_togle_group" title="pat_table_togle_group" className="w3-container w3-padding-small patients-table-toggle-group">
                <button
                  type="button"
                  className="patients-table-toggle"
                  onClick={() => setPatientsTableExpanded((prev) => !prev)}
                  aria-expanded={patientsTableExpanded}
                  aria-controls="patients-table-panel"
                >
                  <span className="patients-table-toggle__title">Patients</span>
                  <FontAwesomeIcon
                    icon={patientsTableExpanded ? faChevronUp : faChevronDown}
                    className={`patients-table-toggle__icon ${patientsTableExpanded ? "is-expanded" : "is-collapsed"}`}
                  />
                </button>
              </div>
              <div
                id="patients-table-panel" title="patients-table-panel"
                className="w3-container w3-padding-small form-content-scroll patients-table-panel patients-table-panel--primary"
                aria-hidden={!patientsTableExpanded}
                style={getScrollablePanelStyle(displayedPatients.length, 6, true,
                  patientsTableExpanded)}
              >
                {patientsTableExpanded ? patientsTableContent : null}
              </div>
              <div id="patients-gender_info" title="patients-gender_info" className="w3-margin" style={{ textAlign: "left" }}>
                <label>{`${t("patients_total", { defaultValue: "Total Patients"})}: ${displayedPatients?.length}, 
                ${t("male", { defaultValue: "Male" }).toLowerCase()}: ${malePatients}, 
                ${t("female", { defaultValue: "Female" }).toLowerCase()}: ${femalePatients},
                ${t("not_specified", { defaultValue: "Not specified" }).toLowerCase()}: 
                ${displayedPatients?.length - malePatients - femalePatients}`}</label>
              </div>
              <div id="stats_table_togle_group" title="stats_table_togle_group" className="w3-container w3-padding-small patients-table-toggle-group">
                <button
                  type="button"
                  className="patients-table-toggle"
                  onClick={() => setStatisticsTableExpanded((prev) => !prev)}
                  aria-expanded={statisticsTableExpanded}
                  aria-controls="statistics-table-panel"
                >
                  <span className="patients-table-toggle__title">
                    {t("statistics", { defaultValue: "Statistics" })}
                  </span>
                  <FontAwesomeIcon
                    icon={statisticsTableExpanded ? faChevronUp : faChevronDown}
                    className={`patients-table-toggle__icon 
                    ${statisticsTableExpanded ? "is-expanded" : "is-collapsed"}`}
                  />
                </button>
              </div>
              <div
                id="statistics-table-panel" title="statistics-table-panel"
                className="form-content-scroll patients-table-panel patients-table-panel--stacked"
                aria-hidden={!statisticsTableExpanded}
                style={getScrollablePanelStyle(statisticsRows.length, 5, false,
                  statisticsTableExpanded)}
              >
                {statisticsTableExpanded ? statisticsTableContent : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Patients;
